<template>
  <fixed-q-select
    ref="selectRef"
    v-bind="propsAndAttrs"
    :option-value="selectOptionValueKey"
    :option-label="selectOptionLabelKey"
    :emit-value="emitValue"
    :map-options="mapOptions"
    :disable="isDisabled"
    :loading="loading"
    :model-value="modelValue"
    :options="options"
    :title="title"
    dropdown-icon="tmi-arrow-down"
    :hide-dropdown-icon="hideAppendIcon || hideDropdownIcon"
    :label="modelValue?.length || useInput ? undefined : placeholder"
    :placeholder="placeholderComputed"
    class="tm-field-select"
    :class="[
      fieldClass,
      {
        'hide-selected-value': (focused || isFocused) && hasSearch,
        'tm-field-select--flat': flat,
        'tm-field-select--auto-height': autoHeight,
        [`tm-field-select--${kebabSize}`]: size,
        'tm-field-select--transparent': transparent,
        'tm-field-select--focused': focused || isFocused,
        'tm-field-select--error': errorState,
        'tm-field-select--small-padding': smallPadding,
        'tm-field-select--appended': appendedToField,
        'tm-field-select--hide-append-icon': hideAppendIcon,
      },
    ]"
    :popup-content-class="internalPopupContentClass"
    :popup-content-style="popupContentStyle"
    behavior="menu"
    :virtual-scroll-item-size="40"
    :use-input="useInput"
    :multiple="multiple"
    :clearable="clearable && !hideAppendIcon"
    :tabindex="tabIndex"
    :bottom-slots="innerErrorState || !!$slots.error || !!hint || !!$slots.hint"
    outlined
    dense
    @update:model-value="onInput"
    @focus="onFocus"
    @blur="onBlur"
    @input-value="onInputValue"
    @keyup="onKeyUp"
    @paste="onPaste"
    @popup-hide="onPopupHide"
    @popup-show="onPopupShow"
    @clear="onClear"
    @virtual-scroll="$emit('virtual-scroll', $event)"
  >
    <template
      v-if="!$slots['selected']"
      v-slot:selected-item="data"
    >
      <slot
        name="selected-item"
        v-bind="data"
      >
        <span class="truncate">
          <slot
            name="selected-item-truncate"
            v-bind="data"
          >
            {{ data.opt.text }}
          </slot>
        </span>
      </slot>
    </template>

    <template v-slot:option="data">
      <q-item
        v-bind="data.itemProps"
        :class="{
          'q-item--subheader caption-12': data.opt.subheader,
          'q-item--border-top': data.opt.newContactOption || data.opt.borderTop,
          'q-item--autoheight': optionAutoHeight,
          'tm-field-select__option--small-padding': smallPadding,
          disabled: data.opt.disabled,
        }"
        :clickable="!data.opt.disabled && !data.opt.unselectable"
        @click="onClickByOption(data.opt)"
        @mouseleave="onOptionMouseLeave"
      >
        <slot
          name="option"
          v-bind="data"
        >
          <span class="truncate">{{ data.opt.text }}</span>
        </slot>
      </q-item>
    </template>

    <template v-slot:no-option="data">
      <slot name="before-options" />
      <slot
        v-if="!hideNoResult"
        name="no-option"
        v-bind="data"
      >
        <q-item v-bind="data.itemProps">
          <q-item-section class="text-grey">
            {{ noResultsText || $t('common.noResults') }}
          </q-item-section>
        </q-item>
      </slot>
    </template>

    <template
      v-if="$slots['append']"
      v-slot:append
    >
      <slot name="append" />
    </template>
    <template v-slot:after>
      <slot name="after" />
    </template>
    <template v-slot:before-options>
      <slot name="before-options" />
    </template>
    <template v-slot:after-options>
      <slot name="after-options" />
    </template>
    <template v-slot:selected>
      <slot name="selected" />
    </template>
    <template v-slot:hint>
      <div>
        <div
          v-if="innerErrorState"
          class="mb-1 body-text-regular-12 error--text"
        >
          <slot name="error">
            <tm-error
              :field="name ?? 'select'"
              :errors="errorsBag"
              :server-errors="serverErrors"
              :invalid="innerErrorState"
            />
          </slot>
        </div>

        <div>
          <slot name="hint">{{ hint }}</slot>
        </div>
      </div>
    </template>
    <template
      v-if="$slots['prepend']"
      v-slot:prepend
    >
      <slot name="prepend" />
    </template>
    <template
      v-if="$slots['label']"
      v-slot:label
    >
      <slot name="label" />
    </template>
  </fixed-q-select>
</template>

<script lang="ts">
import { kebabCase, debounce, isNumber } from 'lodash-es'
import type { QInputProps } from 'quasar'
import {
  defineComponent,
  ref,
  computed,
  type PropType,
  watch,
  nextTick,
  useEventListener,
} from '@/composition/vue/compositionApi'
import { useField } from '@/composition/forms/useField'
import { fieldProps } from '@/components/forms/fieldBase'
import type { SelectValue, SelectOption } from '@/services/forms/types'
import { selectOptionValueKey, selectOptionLabelKey, fieldEmits } from '@/services/forms/types'
import { TmComponentError } from '@/core/error/TmComponentError'
import FixedQSelect from '@/components/shared/quasar/FixedQSelect.vue'
import type { SizeProp } from '@/components/shared/types'
import { passRefCallbacks } from '@/utils/renderFunction'
import { useScrollInject } from '@/composition/scroll'
import { useFormInputCallback } from '@/composition/forms'
import type { StylesheetSizeProp } from '@/types'
import { useFieldSelectTabOwnership } from '@/composition/forms/fieldSelect'
import { useStatelessFormControl } from '@/composition/forms/useStatelessFormControl'
import type { IControl } from '@/composition/forms/types'
import TmError from '@/components/forms/TmError.vue'
import TmLogicError from '@/core/error/tmLogicError'
import type { QFieldNativeStyle } from '@/components/forms/types'
import { isHtmlElement } from '@/utils/string/html/html'
import { keyCodes } from '@/constants/keyCodes'

type Value = SelectValue | Array<SelectValue | Record<string, unknown>>
type SelectComponent = InstanceType<typeof FixedQSelect>
export type FieldSelectSize = 'xSmall' | 'small' | 'medium' | ''
export type FieldSelectVirtualScrollDetails = {
  index: number
  from: number
  to: number
  direction: 'increase' | 'decrease'
}
export const fieldSelectMenuClass = 'simple-select-dropdown'

export default defineComponent({
  components: {
    FixedQSelect,
    TmError,
  },
  props: {
    ...fieldProps,
    open: {
      type: Boolean,
    },
    modelValue: {
      type: [Array, String] as PropType<Value>,
      required: true,
    },
    options: {
      type: Array as PropType<SelectOption[]>,
    },
    title: {
      type: String,
    },
    useInput: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    optionAutoHeight: {
      type: Boolean,
      default: false,
    },
    autoHeight: {
      type: Boolean,
      default: false,
    },
    popupContentClass: {
      type: String,
      default: '',
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    fieldClass: {
      type: String,
    },
    permanentPlaceholder: {
      type: Boolean,
      default: false,
    },
    errorState: {
      type: Boolean,
      default: false,
    },
    hideNoResult: {
      type: Boolean,
    },
    noResultsText: {
      type: String,
    },
    size: {
      type: String as SizeProp<FieldSelectSize>,
      default: '',
    },
    emitValue: {
      type: Boolean,
      default: true,
    },
    mapOptions: {
      type: Boolean,
      default: true,
    },
    forcePlaceholder: {
      type: String,
    },
    popupWidth: {
      type: String as PropType<StylesheetSizeProp>,
    },
    popupWithoutMaxHeight: {
      type: Boolean,
    },
    // menuFit makes the behavior of the QMenu width equal to the anchor width.
    // Important, if you use the quasar props "menuShrink: true", this will break this behavior.
    menuFit: {
      type: Boolean,
      default: true,
    },
    smallPadding: {
      type: Boolean,
    },
    appendedToField: {
      type: Boolean,
    },
    readonly: {
      type: Boolean,
    },
    preventScrollClose: {
      type: Boolean,
    },
    type: {
      type: String as PropType<QInputProps['type']>,
    },
    qFieldNativeStyle: {
      type: Object as PropType<QFieldNativeStyle>,
    },
    autoHighlightFirstOption: {
      type: Boolean,
    },
    hideDropdownIcon: {
      type: Boolean,
    },
  },
  emits: {
    ...fieldEmits<Value>(),
    'input-value': (_value: string) => true,
    focus: (_event: FocusEvent) => true,
    blur: (_event: FocusEvent) => true,
    'click-by-option': (_e: unknown) => true,
    paste: (_event: ClipboardEvent) => true,
    'popup-show': () => true,
    'popup-hide': () => true,
    select: (_e: SelectOption) => true,
    keyup: (_e: KeyboardEvent) => true,
    clear: () => true,
    'virtual-scroll': (_details: FieldSelectVirtualScrollDetails) => true,
  },
  setup(props, context) {
    const kebabSize = computed(() => kebabCase(props.size))

    // Exclude camelCase `tabIndex` from the attr spread: it leaks onto `.q-field__native` as a
    // `tabindex` attribute (via Quasar's splitAttrs), creating a second tab stop inside the field.
    // The real tabindex is bound explicitly as `:tabindex="tabIndex"` on the focus-target input.
    const { propsAndAttrs, emitChangeAndInput } = useField<Value>(props, context, [
      'disabled',
      'class',
      'tabIndex',
      'hideDropdownIcon',
    ])

    let control: IControl | null = null
    if (props.isStateless) {
      const data = useStatelessFormControl({
        reset: () => onInput(null),
      })
      control = data.control
    }
    const errorsBag = computed(() => control?.getErrors() ?? props.errors)
    const innerErrorState = computed(
      () => props.errorState || errorsBag.value.length > 0 || props.serverErrors.length > 0,
    )

    const selectRef = ref<SelectComponent>()
    const focused = ref(false)
    const searchQuery = ref('')

    const hasSearch = computed(() => !!searchQuery.value)

    const hasEmptyOption = computed(() => props.options?.find(({ value }) => !value))

    const hideAppendIcon = computed(() => (props.transparent && !focused.value) || props.readonly)

    const qFieldNativeMinHeight = computed(() => props.qFieldNativeStyle?.minHeight ?? '26px')

    const qFieldNativeGap = computed(() => props.qFieldNativeStyle?.gap ?? '10px')

    const qFieldNativePaddingTop = computed(() => props.qFieldNativeStyle?.paddingTop ?? '0')

    const qFieldNativePaddingRight = computed(() => props.qFieldNativeStyle?.paddingRight ?? '0')

    const qFieldNativePaddingBottom = computed(() => props.qFieldNativeStyle?.paddingBottom ?? '0')

    const qFieldNativePaddingLeft = computed(() => props.qFieldNativeStyle?.paddingLeft ?? '13px')

    const onInput = (value: Value | null | undefined) => {
      // disallow to clear value if prop "clearable === false"
      if (!props.clearable && !hasEmptyOption.value && (value === null || value === undefined)) {
        return
      }
      let normalizedValue: Value
      // Quasar emits null when we clear the value, undefined when we press backspace on keyboard
      if (value === null || value === undefined) {
        normalizedValue = props.multiple ? [] : ''
      } else {
        normalizedValue = value
      }
      emitChangeAndInput(normalizedValue)
    }
    const onFocus = (event: FocusEvent) => {
      focused.value = true
      context.emit('focus', event)
    }
    const onBlur = (event: FocusEvent) => {
      focused.value = false
      context.emit('blur', event)
      if (props.isStateless) {
        control?.handleBlur()
      }
    }
    const onInputValue = (search: string) => {
      context.emit('input-value', search)
      searchQuery.value = search
    }
    const { isTabPressedInsideField } = useFieldSelectTabOwnership(() => isPopupOpen.value || focused.value)

    const onKeyUp = debounce((event: KeyboardEvent) => {
      // Prevent double triggering
      context.emit('keyup', event)
      if (event.key === keyCodes.keyTab) {
        if (event.shiftKey) {
          return
        }
        if (isTabPressedInsideField()) {
          return
        }
        showPopup()
      }
    })

    const onPaste = (event: ClipboardEvent) => {
      context.emit('paste', event)
    }
    const onPopupHide = () => {
      isPopupOpen.value = false
      context.emit('popup-hide')
    }
    const onPopupShow = async () => {
      isPopupOpen.value = true
      highlightFirstOption()
      context.emit('popup-show')
    }
    const onClear = () => {
      context.emit('clear')
    }

    // We should focus element before showPopup
    const showPopup = async () => {
      const selectComponent = selectRef.value

      if (!selectComponent) {
        return
      }

      const selectElement = selectComponent.$el.querySelector('.q-field__control') as HTMLElement

      if (!selectElement) {
        return
      }

      selectElement.focus()

      await nextTick()

      selectElement.click()
    }

    const focus = () => {
      if (props.isDisabled) {
        return
      }
      selectRef.value?.focus()
    }

    useFormInputCallback({
      focus: () => {
        if (props.isDisabled) {
          return
        }
        focused.value = true
        showPopup()
      },
    })

    const clearSearchQuery = () => {
      if (!selectRef.value) {
        throw new TmComponentError('Select ref is undefined')
      }
      selectRef.value.updateInputValue('')
    }

    const refreshMenu = () => {
      if (!selectRef.value) {
        throw new TmComponentError('Select ref is undefined')
      }
      selectRef.value.refresh()
    }

    const placeholderComputed = computed(() => {
      if (props.forcePlaceholder) {
        return props.forcePlaceholder
      }
      let val: boolean | 0 | Value
      if (props.multiple) {
        val = props.modelValue && props.modelValue.length && !props.permanentPlaceholder
      } else {
        val = props.modelValue || !props.useInput
      }

      return val ? undefined : props.placeholder
    })

    const internalPopupContentClass = computed(() => {
      const popupContentClass = props.popupContentClass ? props.popupContentClass : ''
      const menuFitClass = props.menuFit ? 'simple-select-dropdown--menu-fit' : ''
      const popupWithoutMaxHeightClass = props.popupWithoutMaxHeight
        ? `${fieldSelectMenuClass}--without-max-height`
        : ''

      return `${fieldSelectMenuClass} ${popupWithoutMaxHeightClass} ${popupContentClass} ${menuFitClass} `
    })

    const hidePopup = () => selectRef.value?.hidePopup()
    const updateMenuPosition = () => selectRef.value?.updateMenuPosition()
    useScrollInject(() => {
      if (props.preventScrollClose) {
        updateMenuPosition()
      } else {
        hidePopup()
      }
    })

    const popupContentStyle = computed(() => {
      const styles: Record<string, string> = {}
      if (props.popupWidth) {
        styles.width = props.popupWidth
      }
      return styles
    })

    const onClickByOption = (opt: SelectOption) => {
      context.emit('click-by-option', opt.entity)
      context.emit('select', opt)
    }

    const onOptionMouseLeave = () => {
      selectRef.value?.setOptionIndex(-1)
    }

    const isPopupOpen = ref(false)

    const isOptionSelectable = (opt?: SelectOption) =>
      !!opt && !opt.subheader && !opt.notForSearch && !opt.disabled && !opt.unselectable

    const getFirstSelectableIndex = () => props.options?.findIndex(isOptionSelectable) ?? -1

    const getSelectableIndexes = () =>
      (props.options ?? []).reduce<number[]>((acc, option, index) => {
        if (isOptionSelectable(option)) {
          acc.push(index)
        }
        return acc
      }, [])

    const navigateOptions = (direction: 1 | -1) => {
      if (!selectRef.value) {
        return
      }
      const indexes = getSelectableIndexes()
      if (indexes.length === 0) {
        return
      }
      const current = selectRef.value.getOptionIndex()
      const currentPosition = indexes.indexOf(current)
      let nextPosition: number
      if (currentPosition === -1) {
        nextPosition = direction === 1 ? 0 : indexes.length - 1
      } else {
        nextPosition = (currentPosition + direction + indexes.length) % indexes.length
      }
      const nextIndex = indexes[nextPosition]
      selectRef.value.setOptionIndex(nextIndex)
      selectRef.value.scrollTo(nextIndex)
    }

    let highlightToken = 0
    const highlightFirstOption = () => {
      if (!props.autoHighlightFirstOption) {
        return
      }
      highlightToken += 1
      const token = highlightToken
      setTimeout(() => {
        if (token !== highlightToken || !isPopupOpen.value || !selectRef.value) {
          return
        }
        const index = getFirstSelectableIndex()
        if (index >= 0) {
          selectRef.value.setOptionIndex(index)
          selectRef.value.scrollTo(index)
        }
      })
    }

    const selectHighlightedOption = () => {
      if (!selectRef.value) {
        return false
      }
      const highlightedIndex = selectRef.value.getOptionIndex()
      const index = isNumber(highlightedIndex) && highlightedIndex >= 0 ? highlightedIndex : getFirstSelectableIndex()
      const option = index >= 0 ? props.options?.[index] : undefined
      if (option === undefined || !isOptionSelectable(option)) {
        return false
      }
      selectRef.value.toggleOption(option)
      onClickByOption(option!)
      if (props.multiple) {
        clearSearchQuery()
      }
      return true
    }

    const onFieldKeydownCapture = (event: KeyboardEvent) => {
      if (!isPopupOpen.value || !selectRef.value) {
        return
      }

      // Single-select dropdowns are navigated with the arrow keys only: dropping the highlight makes
      // Quasar's own Tab handling just close the popup instead of selecting the highlighted option.
      if (event.key === keyCodes.keyTab) {
        selectRef.value.setOptionIndex(-1)
        return
      }

      if (!props.autoHighlightFirstOption) {
        return
      }

      const target = event.target
      if (!isHtmlElement(target) || !(target instanceof HTMLInputElement)) {
        return
      }

      if (event.key === keyCodes.keyDown || event.key === keyCodes.keyUp) {
        event.preventDefault()
        event.stopPropagation()
        navigateOptions(event.key === keyCodes.keyUp ? -1 : 1)
        return
      }

      if (event.key === keyCodes.keyEnter && selectHighlightedOption()) {
        event.preventDefault()
        event.stopPropagation()
      }
    }

    const isOptionsAppend = (newOptions?: SelectOption[], oldOptions?: SelectOption[]) =>
      !!newOptions &&
      !!oldOptions &&
      oldOptions.length > 0 &&
      newOptions.length > oldOptions.length &&
      oldOptions.every((option, index) => option.value === newOptions[index].value)

    watch(
      () => props.options,
      (newOptions, oldOptions) => {
        // re-highlighting on append would scroll the menu back to top
        if (isPopupOpen.value && !isOptionsAppend(newOptions, oldOptions)) {
          highlightFirstOption()
        }
      },
    )

    useEventListener(() => selectRef.value?.$el, 'keydown', onFieldKeydownCapture, true)

    watch(
      () => [props.open, selectRef.value] as [typeof props.open, SelectComponent],
      (newValue, oldValue) => {
        const [newIsOpen, selectComponent] = newValue
        const [oldIsOpen] = oldValue || []

        if (!selectComponent) {
          return
        }

        if (newIsOpen) {
          setTimeout(() => {
            showPopup()
          }, 50)
          return
        }

        if (oldIsOpen !== undefined) {
          hidePopup()
        }
      },
      { immediate: true },
    )

    watch(focused, (value) => {
      if (!value) {
        return
      }
      if (!selectRef.value) {
        throw new TmLogicError('selectRef.value is null')
      }
      const selecteHtmlElement = selectRef.value.$el
      if (!(selecteHtmlElement instanceof HTMLElement)) {
        throw new TmLogicError('selecteHtmlElement should be instanceof HTMLElement')
      }
      const inputs = selecteHtmlElement.querySelectorAll('input')
      const filteredInputs = Array.from(inputs).filter((t) => {
        return !t.classList.contains('q-select__autocomplete-input')
      })
      if (filteredInputs.length > 1) {
        throw new TmLogicError('Should be only one input element')
      }
      const input = filteredInputs[0]
      if (!input) {
        throw new TmLogicError('input is null')
      }
      input.focus()
    })

    return {
      kebabSize,
      selectRef,
      clearSearchQuery,
      selectOptionLabelKey,
      selectOptionValueKey,
      focused,
      hasSearch,
      hideAppendIcon,
      onInputValue,
      onFocus,
      onBlur,
      onInput,
      onKeyUp,
      onPaste,
      onPopupHide,
      onPopupShow,
      onClear,
      refreshMenu,
      propsAndAttrs,
      placeholderComputed,
      popupContentStyle,
      internalPopupContentClass,
      kebabCase,
      // methods for using from parents
      focus,
      ...passRefCallbacks(selectRef, [
        'blur',
        'hidePopup',
        'refresh',
        'scrollTo',
        'setOptionIndex',
        'showPopup',
        'updateInputValue',
        'updateMenuPosition',
      ]),
      onClickByOption,
      onOptionMouseLeave,
      errorsBag,
      innerErrorState,
      qFieldNativeMinHeight,
      qFieldNativeGap,
      qFieldNativePaddingTop,
      qFieldNativePaddingRight,
      qFieldNativePaddingBottom,
      qFieldNativePaddingLeft,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@/styles/quasar-field.scss';
@import '@/styles/mixins.scss';

$qFieldLabelFontSize: $font-size-root;
$qFieldLabelLineHeight: 1.25;

.tm-field-select {
  @include quasar-field;

  &__option {
    &--small-padding {
      padding-left: 8px !important;
      padding-right: 8px !important;
    }
  }

  &--auto-height {
    &:deep(.q-field__control) {
      height: auto !important;
    }
  }

  &--small-padding {
    :deep(.q-field__control) {
      .q-field__native {
        padding-left: 8px !important;
      }
    }
  }

  &--error {
    @include quasar-field-error;
  }

  &--auto-height {
    :deep(.q-field__control) {
      // Placeholder is rendered as a Quasar label, which pads labeled auto-height fields for a floating label
      .q-field__control-container {
        padding-top: 0;
      }
      .q-field__label {
        top: calc(50% - $qFieldLabelFontSize * $qFieldLabelLineHeight / 2) !important;
      }
      .q-field__native {
        padding: 10px 0 10px 13px !important;
      }
    }

    &.tm-field-select--small {
      :deep(.q-field__control) {
        .q-field__native {
          padding: 6px 0 6px 8px !important;
        }
      }
    }

    &.tm-field-select--medium {
      :deep(.q-field__control) {
        .q-field__native {
          padding: 8px 0 8px 13px !important;
        }
      }
    }
  }

  // default style
  :deep(.q-field__control) {
    // right icon position
    @include field-right-icon();
    // clear button
    @include field-right-icon-cancel();
    // selected item
    .q-field__native {
      min-height: v-bind(qFieldNativeMinHeight);
      padding-top: v-bind(qFieldNativePaddingTop);
      padding-right: v-bind(qFieldNativePaddingRight);
      padding-bottom: v-bind(qFieldNativePaddingBottom);
      padding-left: v-bind(qFieldNativePaddingLeft);
      gap: v-bind(qFieldNativeGap);
      color: $emphasize;
      font-size: $font-size-root;
      font-weight: $regular-font;

      .q-badge {
        margin-top: 2px;
        margin-bottom: 2px;
      }
    }
    input {
      padding-left: 0 !important;
      text-overflow: ellipsis;
      min-width: 10px !important;
      align-self: center;
    }
    // imitate placeholder
    .q-field__label {
      position: absolute !important;
      left: 13px;
      right: 0;
      transform: none !important;
      color: $light;
      font-weight: $regular-font;
      font-size: $qFieldLabelFontSize;
      line-height: $qFieldLabelLineHeight;
      transition: color $transition-300ms-ease-in-out;
      &:before {
        content: none;
      }
    }
  }

  &:not(&--focused) :deep(.q-field__control input) {
    padding-right: 13px;
  }

  // line adjustment for single select
  &:not(.q-select--multiple):deep(.q-field__native) {
    flex-wrap: nowrap;
  }

  // focused state
  &--focused :deep(.q-field__control) {
    .q-field__append button {
      opacity: 1;
      visibility: visible;
    }
  }
  &.q-field--focused:deep(.q-field__control) {
    // imitate placeholder
    .q-field__label {
      text-overflow: initial;
      color: $light;
    }
    // clear button
    .q-field__append button {
      visibility: visible;
    }
  }

  // sizes
  &.tm-field-select--x-small :deep(.q-field__control) {
    height: 28px;
    min-height: 28px;
    .q-field__native {
      min-height: auto;
      font-size: $sm-font;
      padding: 0 0 0 13px;
    }
    .q-field__append {
      height: 28px;
      min-height: 28px;
      font-size: 20px;
      padding-left: 6px;
      margin-right: 8px;
    }
  }
  &.tm-field-select--small :deep(.q-field__control) {
    height: 32px;
    min-height: 32px;
    .q-field__label {
      top: 6px;
      left: 8px;
    }
    .q-field__native {
      white-space: nowrap;
      overflow: hidden;
      min-height: auto;
      padding: 0 0 0 8px;
      gap: 8px;
    }
    .q-field__append {
      height: 32px;
      min-height: 32px;
    }
  }

  &.q-select--multiple {
    &:deep(.q-field__control) {
      cursor: pointer;
    }
  }

  &--medium :deep(.q-field__control) {
    height: 36px;
    min-height: 36px;

    .q-field__append {
      height: 36px;
      min-height: 36px;
    }
  }

  &--appended {
    :deep(.q-field__control) {
      background: $input !important;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
}
</style>
